import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, inboxApi, type Conversation, type ForwardResult, type Message } from '@wa/shared';
import { Avatar } from '@/components/Avatar';

interface Props {
  message: Message;
  /** The chat the message is in — listed, but not pre-ticked: forwarding a message to its own thread is never what was meant. */
  currentConversationId: number;
  onClose: () => void;
}

/** The endpoint's cap; more than this is a broadcast, which has its own screen. */
const MAX_TARGETS = 20;

/**
 * Forward a message to other chats — the phone's long-press → Forward.
 *
 * A search over the same list the inbox shows, tick the chats, send. The answer
 * is per chat, because each forward is its own send under the same rules as any
 * send: a chat whose 24-hour window has closed is refused (WhatsApp has no
 * forward that reaches past it — that needs a template), a blocked or opted-out
 * contact is refused, and the rest go. The sheet stays open showing which went
 * and which did not, rather than closing on the first success and leaving the
 * agent to discover the refusals in the thread list.
 */
export function ForwardSheet({ message, currentConversationId, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Map<number, Conversation>>(new Map());
  const [results, setResults] = useState<ForwardResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The inbox's own list and search: name or number, newest first. Debounced by
  // react-query's key — a keystroke a second is a query a second, which the
  // list endpoint is built for.
  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set('status', 'all');
    if (search.trim()) params.set('q', search.trim());
    return params;
  }, [search]);

  const conversations = useQuery({
    queryKey: ['conversations', 'forward-picker', search.trim()],
    queryFn: () => inboxApi.conversations(query),
  });

  const forward = useMutation({
    mutationFn: (ids: number[]) => inboxApi.forward(message.id, ids),
    onSuccess: (data) => {
      setError(null);
      setResults(data.results);
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      for (const r of data.results) {
        if (r.ok) void queryClient.invalidateQueries({ queryKey: ['messages', r.conversationId] });
      }
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not forward that.'),
  });

  const toggle = (conversation: Conversation) => {
    setPicked((current) => {
      const next = new Map(current);
      if (next.has(conversation.id)) next.delete(conversation.id);
      else if (next.size < MAX_TARGETS) next.set(conversation.id, conversation);
      return next;
    });
  };

  const items = (conversations.data?.items ?? []).filter((c) => c.id !== currentConversationId);
  const preview = message.body
    ? message.body
    : message.mediaFileName ?? `[${message.messageType}]`;

  const nameOf = (id: number) => picked.get(id)?.displayName ?? picked.get(id)?.waId ?? `chat ${id}`;

  return createPortal(
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/35 p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Forward message"
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop)"
      >
        <div className="border-b border-(--color-line) px-4 py-3">
          <h2 className="text-lead font-semibold tracking-tight">Forward to…</h2>
          <p className="mt-0.5 truncate text-micro text-(--color-muted)" title={preview}>
            {message.mediaAssetId ? `${message.messageType}: ` : ''}{preview}
          </p>
        </div>

        {results === null ? (
          <>
            <div className="px-4 pt-3">
              <input
                type="search"
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name or number"
                aria-label="Search chats"
                className="tap h-9 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-3 text-body outline-none transition focus:border-(--color-brand) focus:bg-(--color-surface)"
              />
            </div>

            {picked.size > 0 && (
              <div className="flex flex-wrap gap-1 px-4 pt-2">
                {[...picked.values()].map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c)}
                    className="tap rounded-full bg-(--color-brand-wash) px-2 py-0.5 text-micro text-(--color-ink)"
                    aria-label={`Remove ${c.displayName ?? c.waId}`}
                  >
                    {c.displayName ?? c.waId} ×
                  </button>
                ))}
              </div>
            )}

            <ul className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {conversations.isPending && <li className="px-2 py-3 text-small text-(--color-muted)">Loading…</li>}
              {!conversations.isPending && items.length === 0 && (
                <li className="px-2 py-3 text-small text-(--color-muted)">No chats match.</li>
              )}
              {!conversations.isPending && items.length > 0 && items.every((c) => !(c.windowExpiresAt && new Date(c.windowExpiresAt) > new Date())) && (
                <li className="px-2 pb-2 text-micro text-(--color-muted)">
                  None of these chats can take a forward right now: their 24-hour windows are closed. Send a template from the chat instead.
                </li>
              )}
              {items.map((c) => {
                const on = picked.has(c.id);
                // A closed 24-hour window cannot take a forward — WhatsApp has no
                // forward that bypasses the rule; only a template reaches past it.
                // Shown, not hidden, so the agent sees the chat exists and why it
                // cannot be picked, rather than searching for a name that never appears.
                const open = !!c.windowExpiresAt && new Date(c.windowExpiresAt) > new Date();
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      aria-disabled={!open}
                      disabled={!open}
                      onClick={() => toggle(c)}
                      className={[
                        'tap flex w-full items-center gap-3 rounded-md px-2 py-2 text-left',
                        open ? 'hover:bg-(--color-sunken)' : 'cursor-not-allowed opacity-55',
                        on ? 'bg-(--color-brand-wash)' : '',
                      ].join(' ')}
                    >
                      <Avatar name={c.displayName} waId={c.waId} size={32} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-small font-medium text-(--color-ink)">{c.displayName ?? c.waId}</span>
                        <span className="block truncate text-micro text-(--color-muted)">
                          {c.channelName ?? c.channelNumber ?? ''}
                          {open ? '' : ' · window closed — needs a template'}
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        className={[
                          'grid h-5 w-5 shrink-0 place-items-center rounded-full border',
                          on ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-ink)' : 'border-(--color-line)',
                        ].join(' ')}
                      >
                        {on && (
                          <svg viewBox="0 0 20 20" width="12" height="12"><path d="M5 10.5l3.2 3L15 6.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {error && <p role="alert" className="px-4 text-small text-(--color-bad)">{error}</p>}

            <div className="flex items-center justify-between gap-2 border-t border-(--color-line) px-4 py-3">
              <span className="text-micro text-(--color-muted)">
                {picked.size === 0 ? 'Pick up to 20 chats' : `${picked.size} selected`}
              </span>
              <span className="flex gap-2">
                <button type="button" onClick={onClose}
                        className="tap rounded-md px-3 py-1.5 text-small text-(--color-muted) hover:text-(--color-ink)">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={picked.size === 0 || forward.isPending}
                  onClick={() => forward.mutate([...picked.keys()])}
                  className="tap rounded-md bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-60"
                >
                  {forward.isPending ? 'Forwarding…' : 'Forward'}
                </button>
              </span>
            </div>
          </>
        ) : (
          <>
            <ul className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-small">
              {results.map((r) => (
                <li key={r.conversationId} className="flex items-start gap-2 py-1.5">
                  <span className={r.ok ? 'text-(--color-good)' : 'text-(--color-bad)'} aria-hidden="true">
                    {r.ok ? '✓' : '✕'}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium text-(--color-ink)">{nameOf(r.conversationId)}</span>
                    {!r.ok && <span className="block text-micro text-(--color-muted)">{r.problem}</span>}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end border-t border-(--color-line) px-4 py-3">
              <button type="button" onClick={onClose}
                      className="tap rounded-md bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink)">
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
